import React, { useEffect, useState } from 'react';
import { FaEye, FaEdit, FaBan, FaDownload, FaPlus, FaSort, FaSortUp, FaSortDown, FaCheck } from 'react-icons/fa';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import VendorFormModal from './VendorFormModal';
import VendorViewModal from './VendorViewModal';
import Sidebar from './Sidebar';

const API_URL = '/api';
const VendorMaster = () => {
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showView, setShowView] = useState(false);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [isScrolled, setIsScrolled] = useState(false);
  const [isOnboardMode, setIsOnboardMode] = useState(false);
  const [selectedVendors, setSelectedVendors] = useState([]);
  const navigate = useNavigate(); 
  
  useEffect(() => {
    fetchVendors();
  }, []);
  
  // Add scroll listener for sticky header behavior
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      setIsScrolled(scrollTop > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  
  const fetchVendors = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/vendors`);
      const data = await res.json();
      setVendors(data);
    } catch {
      setVendors([]);
    }
    setLoading(false);
  };
  
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
    const sortedVendors = [...vendors].sort((a, b) => {
      if (a[key] === null) return 1;
      if (b[key] === null) return -1;
      
      // Handle date fields
      if (key === 'created_at') {
        return direction === 'asc' 
          ? new Date(a[key]) - new Date(b[key])
          : new Date(b[key]) - new Date(a[key]);
      }
      
      // Handle numeric fields
      if (key === 'vendor_code') {
        const aNum = parseInt(a[key].split('-')[1]);
        const bNum = parseInt(b[key].split('-')[1]);
        return direction === 'asc' ? aNum - bNum : bNum - aNum;
      }
      // Handle string fields
      if (a[key] < b[key]) return direction === 'asc' ? -1 : 1;
      if (a[key] > b[key]) return direction === 'asc' ? 1 : -1;
      return 0;
    });
    setVendors(sortedVendors);
  };
  
  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return <FaSort className="ml-1" />;
    return sortConfig.direction === 'asc' 
      ? <FaSortUp className="ml-1" /> 
      : <FaSortDown className="ml-1" />;
  };
  
  const handleView = (vendor) => {
    setSelectedVendor(vendor);
    setShowView(true);
  };
  
  const handleEdit = (vendor) => {
    setSelectedVendor(vendor);
    setIsEdit(true);
    setShowForm(true);
  };
  
  const handleDeactivate = async (vendor) => {
    if (!window.confirm('Are you sure you want to deactivate this vendor?')) return;
    
    try {
      const response = await fetch(`${API_URL}/vendors/${vendor.vendor_code}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...vendor, is_active: 0 }),
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to deactivate vendor');
      }
      
      fetchVendors();
    } catch (error) {
      console.error('Error deactivating vendor:', error);
      alert(error.message || 'An error occurred while deactivating the vendor');
    }
  };
  
  const generatePassword = (length = 10) => {
    const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789@#$!";
    let pwd = "";
    for (let i = 0; i < length; i++) {
      pwd += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return pwd;
  };
  
  const handleOnboard = async (vendor) => {
  if (!vendor) return;
  
  const autoPassword = generatePassword(12);
  const createdAt = new Date().toISOString(); // device local time (in ISO format)

  const formData = new FormData();
  formData.append("company_name", vendor.vendor_name);
  formData.append("email_id", vendor.email);
  formData.append("mobile_no", vendor.phone_number);
  formData.append("password", autoPassword);
  formData.append("vendor_code", vendor.vendor_code);
  formData.append("createdAt", createdAt);  // ✅ send timestamp too

  try {
    const response = await fetch(`${API_URL}/clients`, {
      method: "POST",
      body: formData,
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || "Failed to onboard client");
    }
    
    const data = await response.json();
    return { success: true, vendorName: vendor.vendor_name, clientCode: data.client_code };
  } catch (error) {
    console.error("Error onboarding client:", error);
    return { success: false, error: error.message || "An error occurred while onboarding the client" };
  }
};

  const handleReactivate = async (vendor) => {
    if (!window.confirm('Are you sure you want to reactivate this vendor?')) return;
    
    try {
      const response = await fetch(`${API_URL}/vendors/${vendor.vendor_code}/reactivate`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to reactivate vendor');
      }
      
      fetchVendors();
    } catch (error) {
      console.error('Error reactivating vendor:', error);
      alert(error.message || 'An error occurred while reactivating the vendor');
    }
  };
  
  const handleDownloadCSV = () => {
    const headers = [
      'vendor_code','vendor_name','vendor_type','email','phone_number','country','status','created_at'
    ];
    const rows = vendors.map(v => [
      v.vendor_code, v.vendor_name, v.vendor_type, v.email, v.phone_number, v.country, v.status, dayjs(v.created_at).format('YYYY-MM-DD HH:mm')
    ]);
    const csv = [headers, ...rows].map(r => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'vendors.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };
  
  const handleCreate = () => {
    setSelectedVendor(null);
    setIsEdit(false);
    setShowForm(true);
  };
  
  const handleFormSubmit = async (formData) => {
    setShowForm(false);
    fetchVendors();
  };
  
  const handleLogout = () => {
    navigate('/login');
  };
  
  const handleOnboardMode = () => {
    setIsOnboardMode(true);
    setSelectedVendors([]);
  };
  
  const handleVendorSelection = (vendorCode) => {
    if (selectedVendors.includes(vendorCode)) {
      setSelectedVendors(selectedVendors.filter(code => code !== vendorCode));
    } else {
      setSelectedVendors([...selectedVendors, vendorCode]);
    }
  };
  
  const handleOnboardSelected = async () => {
    if (selectedVendors.length === 0) {
      alert('Please select at least one vendor to onboard');
      return;
    }
    
    const results = [];
    for (const vendorCode of selectedVendors) {
      const vendor = vendors.find(v => v.vendor_code === vendorCode);
      if (vendor) {
        const result = await handleOnboard(vendor);
        results.push({ vendor, result });
      }
    }
    
    // Show results summary
    let message = 'Onboarding completed:\n\n';
    results.forEach(({ vendor, result }) => {
      if (result.success) {
        message += `✅ ${vendor.vendor_name} onboarded successfully! Client Code: ${result.clientCode}\n`;
      } else {
        message += `❌ ${vendor.vendor_name} failed: ${result.error}\n`;
      }
    });
    
    alert(message);
    
    // Reset onboard mode
    setIsOnboardMode(false);
    setSelectedVendors([]);
    fetchVendors();
  };
  
  const handleCancelOnboard = () => {
    setIsOnboardMode(false);
    setSelectedVendors([]);
  };
  
  return (
  <div className="min-h-screen flex flex-col md:flex-row bg-[#E5E1DA]">
    {/* Sidebar */}
    <Sidebar onLogout={handleLogout} />

    {/* Main Content */}
    <main className="flex-1 p-2 sm:p-2 md:p-8 overflow-hidden md:ml-16 transition-all duration-300">
      <div className="flex flex-col h-full">

        {/* Header + Buttons */}
<div className="flex flex-col sm:flex-row justify-center sm:justify-between items-center text-center mb-4 sm:mb-6 gap-3">
  <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-[#2b6777]">
    Vendor Master
  </h1>
  <div className="flex flex-wrap justify-center sm:justify-end gap-2">
    {isOnboardMode ? (
      <>
        <button
          className="flex items-center gap-2 bg-red-500 hover:bg-red-600 text-white font-semibold px-3 py-2 sm:px-4 sm:py-2 rounded-lg shadow transition text-sm sm:text-base"
          onClick={handleCancelOnboard}
        >
          Cancel
        </button>
        <button
          className={`flex items-center gap-2 bg-blue-500 hover:bg-blue-600 text-white font-semibold px-3 py-2 sm:px-4 sm:py-2 rounded-lg shadow transition text-sm sm:text-base ${
            selectedVendors.length === 0 ? 'opacity-50 cursor-not-allowed' : ''
          }`}
          onClick={handleOnboardSelected}
          disabled={selectedVendors.length === 0}
        >
          Onboard Selected ({selectedVendors.length})
        </button>
      </>
    ) : (
      <>
        <button
          className="flex items-center gap-2 bg-[#92C7CF] hover:bg-[#AAD7D9] text-white font-semibold px-3 py-2 sm:px-4 sm:py-2 rounded-lg shadow transition text-sm sm:text-base"
          onClick={handleOnboardMode}
        >
          <FaPlus className="text-sm sm:text-base" /> Onboard
        </button>
        {/* <button
          className="flex items-center gap-2 bg-[#92C7CF] hover:bg-[#AAD7D9] text-white font-semibold px-3 py-2 sm:px-4 sm:py-2 rounded-lg shadow transition text-sm sm:text-base"
          onClick={handleCreate}
        >
          <FaPlus className="text-sm sm:text-base" /> New Vendor
        </button> */}
      </>
    )}
  </div>
</div>


        {/* Table Section */}
        <div className="flex-1 overflow-hidden">
          <div className="h-full overflow-auto scroll-smooth">
            <div className="min-w-[800px] md:min-w-full">
              <table className="w-full bg-white rounded-2xl shadow-lg border-separate border-spacing-0 text-xs sm:text-sm">
                <thead>
                  <tr>
                    {isOnboardMode && (
                      <th className="sticky top-0 z-20 bg-[#AAD7D9] w-10 sm:w-12 text-[#2b6777] px-2 sm:px-4 py-3 text-center border-r border-white font-bold rounded-tl-2xl">
                        <input
                          type="checkbox"
                          className="h-4 w-4 rounded border-gray-300 text-[#2b6777] focus:ring-[#2b6777]"
                          checked={selectedVendors.length === vendors.filter(v => v.is_active).length}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedVendors(vendors.filter(v => v.is_active).map(v => v.vendor_code));
                            } else {
                              setSelectedVendors([]);
                            }
                          }}
                        />
                      </th>
                    )}
                    {[
                      { key: 'vendor_code', label: 'Vendor Code' },
                      { key: 'vendor_name', label: 'Vendor Name' },
                      { key: 'vendor_type', label: 'Vendor Type' },
                      { key: 'email', label: 'Email' },
                      { key: 'phone_number', label: 'Phone No.' },
                      { key: 'country', label: 'Country' },
                      { key: 'status', label: 'Status' },
                      { key: 'created_at', label: 'Created At' },
                    ].map((col, idx) => (
                      <th
                        key={col.key}
                        className={`sticky top-0 z-20 bg-[#AAD7D9] text-[#2b6777] px-2 sm:px-4 py-3 text-center font-bold cursor-pointer hover:bg-[#92C7CF]`}
                        onClick={() => handleSort(col.key)}
                      >
                        <div className="flex items-center justify-center gap-1">
                          {col.label} {getSortIcon(col.key)}
                        </div>
                      </th>
                    ))}
                    <th className="sticky top-0 z-20 bg-[#AAD7D9] text-[#2b6777] px-2 sm:px-4 py-3 text-center font-bold rounded-tr-2xl">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td
                        colSpan={isOnboardMode ? 10 : 9}
                        className="text-center py-8 text-[#AAD7D9] font-semibold"
                      >
                        Loading vendors...
                      </td>
                    </tr>
                  ) : vendors.length === 0 ? (
                    <tr>
                      <td
                        colSpan={isOnboardMode ? 10 : 9}
                        className="text-center py-8 text-[#AAD7D9] font-semibold"
                      >
                        No vendors found.
                      </td>
                    </tr>
                  ) : (
                    vendors.map((vendor) => (
                      <tr
                        key={vendor.vendor_code}
                        className={`transition-all duration-300 group ${
                          vendor.is_active ? 'hover:bg-[#eaf6f6]' : 'opacity-60'
                        } border-b last:border-b-0`}
                      >
                        {isOnboardMode && (
                          <td className="px-2 sm:px-4 py-3 text-center text-xs sm:text-sm">
                            {vendor.is_active ? (
                              <input
                                type="checkbox"
                                className="h-4 w-4 rounded border-gray-300 text-[#2b6777] focus:ring-[#2b6777]"
                                checked={selectedVendors.includes(vendor.vendor_code)}
                                onChange={() => handleVendorSelection(vendor.vendor_code)}
                              />
                            ) : (
                              <span className="text-gray-400">-</span>
                            )}
                          </td>
                        )}
                        <td className="px-2 sm:px-4 py-3 text-center font-mono text-xs sm:text-sm text-blue-600 underline cursor-pointer" onClick={() => handleView(vendor)}>
                          {vendor.vendor_code}
                        </td>
                        <td className="px-2 sm:px-4 py-3 text-center font-semibold">{vendor.vendor_name}</td>
                        <td className="px-2 sm:px-4 py-3 text-center">{vendor.vendor_type}</td>
                        <td className="px-2 sm:px-4 py-3 text-center break-all max-w-[120px] sm:max-w-[180px]">
                          {vendor.email}
                        </td>
                        <td className="px-2 sm:px-4 py-3 text-center break-all max-w-[80px] sm:max-w-[120px]">
                          {vendor.phone_number}
                        </td>
                        <td className="px-2 sm:px-4 py-3 text-center">{vendor.country}</td>
                        <td className="px-2 sm:px-4 py-3 text-center">
                          <span
                            className={`inline-block px-2 sm:px-3 py-1 rounded-full text-[10px] sm:text-xs font-bold ${
                              vendor.status === 'Active'
                                ? 'bg-[#52ab98] text-white'
                                : 'bg-gray-300 text-gray-500'
                            }`}
                          >
                            {vendor.status}
                          </span>
                        </td>
                        <td className="px-2 sm:px-4 py-3 text-center text-xs text-gray-700">
                          {vendor.created_at
                            ? dayjs(vendor.created_at).format('YYYY-MM-DD HH:mm')
                            : '-'}
                        </td>
                        <td className="px-2 sm:px-4 py-3 flex gap-1 sm:gap-2 justify-center items-center">
                          <button
                            title="View"
                            className="p-1 sm:p-2 rounded-full hover:bg-[#c8d8e4] text-[#2b6777]"
                            onClick={() => handleView(vendor)}
                          >
                            <FaEye className="text-xs sm:text-sm" />
                          </button>
                          <button
                            title="Edit"
                            className="p-1 sm:p-2 rounded-full hover:bg-[#c8d8e4] text-[#2b6777]"
                            onClick={() => handleEdit(vendor)}
                          >
                            <FaEdit className="text-xs sm:text-sm" />
                          </button>
                          {vendor.is_active ? (
                            <button
                              title="Deactivate"
                              className="p-1 sm:p-2 rounded-full hover:bg-red-100 text-red-500"
                              onClick={() => handleDeactivate(vendor)}
                            >
                              <FaBan className="text-xs sm:text-sm" />
                            </button>
                          ) : (
                            <button
                              title="Reactivate"
                              className="p-1 sm:p-2 rounded-full hover:bg-green-100 text-green-500"
                              onClick={() => handleReactivate(vendor)}
                            >
                              <FaCheck className="text-xs sm:text-sm" />
                            </button>
                          )}
                          <button
                            title="Download CSV"
                            className="p-1 sm:p-2 rounded-full hover:bg-[#c8d8e4] text-[#2b6777]"
                            onClick={handleDownloadCSV}
                          >
                            <FaDownload className="text-xs sm:text-sm" />
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Modals */}
        <VendorFormModal
          open={showForm}
          onClose={() => setShowForm(false)}
          onSubmit={handleFormSubmit}
          initialData={selectedVendor}
          isEdit={isEdit}
          loading={formLoading}
        />
        <VendorViewModal
          open={showView}
          onClose={() => setShowView(false)}
          vendor={selectedVendor}
        />
      </div>
    </main>
  </div>
);

};

export default VendorMaster;